import React, { useState } from "react";
import Header from "../components/Header";
import Podcast from "../components/Podcast";
import CoreValues from "../components/CoreValues";
import Salvation from "../components/Salvation";
import Mission from "../components/Mission";
import News from "../components/News";
import Footer from "../components/Footer";

export default function PrayerRequestPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    request: "",
    consent: true,
  });

  const [status, setStatus] = useState({
    state: "idle", // idle | loading | success | error
    message: "",
  });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", request: "", consent: true });
  };

  const validate = () => {
    if (!form.request || form.request.trim().length < 10) {
      return "Please enter your prayer request (at least 10 characters).";
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Please enter a valid email address (or leave it blank).";
    }
    if (!form.consent) {
      return "Please confirm we may contact you (or uncheck and submit anonymously with no contact info).";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      setStatus({ state: "error", message: err });
      return;
    }

    setStatus({ state: "loading", message: "" });

    try {
      const res = await fetch("/api/sendMail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name?.trim() || "",
          email: form.email?.trim() || "",
          phone: form.phone?.trim() || "",
          request: form.request?.trim() || "",
        }),
      });

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error(`Server returned non-JSON response (HTTP ${res.status}).`);
      }

      if (!res.ok || json.success === false) {
        throw new Error(json.error || `Request failed (HTTP ${res.status}).`);
      }

      setStatus({
        state: "success",
        message: json.message || "Thank you. Your prayer request has been received. We will be praying with you.",
      });

      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      setStatus({
        state: "error",
        message: error?.message || "We couldn’t submit your request right now. Please try again in a moment.",
      });
    }
  };

  return (
    <>
      <Header />
      <hr className="g-brd-gray-light-v4 my-0" />

      <section className="g-bg-white">
        <div className="container g-py-50">
          <h1 className="g-font-weight-300 g-letter-spacing-1 g-mb-2">Prayer Request</h1>
          <p className="mb-0 g-font-size-16">
            Prayer is the foundation of a strong Christian home. It invites God&apos;s presence, strengthens family bonds,
            and nurtures spiritual growth. No concern is too small, and no situation is too big for God.
          </p>
          <p className="g-font-size-14 g-color-gray-dark-v2 g-mt-10 mb-0">
            If you need personal ministry, please contact us. If you include your email, we can follow up.
          </p>
        </div>
      </section>

      {status.state === "success" && (
        <div className="container g-mt-20" style={{ position: "relative", zIndex: 2 }}>
          <div className="alert alert-success g-pa-20" role="alert" style={{ borderRadius: 12 }}>
            <div className="d-flex align-items-start justify-content-between">
              <div>
                <strong>Submitted!</strong> {status.message}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-link g-color-main"
                onClick={() => setStatus({ state: "idle", message: "" })}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="container g-pt-35 g-pb-75">
        <div className="row">
          <div className="col-lg-10 offset-lg-1">
            {status.state === "error" && status.message && (
              <div className="alert alert-danger g-mb-20" role="alert" style={{ borderRadius: 12 }}>
                <strong>Oops!</strong> {status.message}
              </div>
            )}

            <div className="g-brd-around g-brd-gray-light-v4 g-bg-white g-pa-30">
              <h3 className="h5 g-mb-20 g-color-black">Submit a Prayer Request</h3>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 g-mb-20">
                    <label className="g-font-weight-600 g-color-gray-dark-v2 g-mb-5">Name (optional)</label>
                    <input className="form-control" name="name" value={form.name} onChange={onChange} placeholder="Your name" autoComplete="name" />
                  </div>

                  <div className="col-md-6 g-mb-20">
                    <label className="g-font-weight-600 g-color-gray-dark-v2 g-mb-5">Email (optional)</label>
                    <input className="form-control" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@example.com" autoComplete="email" />
                  </div>

                  <div className="col-md-6 g-mb-20">
                    <label className="g-font-weight-600 g-color-gray-dark-v2 g-mb-5">Phone (optional)</label>
                    <input className="form-control" name="phone" value={form.phone} onChange={onChange} placeholder="(###) ###-####" autoComplete="tel" />
                  </div>

                  <div className="col-12 g-mb-20">
                    <label className="g-font-weight-600 g-color-gray-dark-v2 g-mb-5">Prayer Request</label>
                    <textarea className="form-control" name="request" rows={6} value={form.request} onChange={onChange} placeholder="Share what you would like us to pray for..." required />
                    <small className="g-color-gray-dark-v5">Please don&apos;t include highly sensitive information.</small>
                  </div>

                  {/* ✅ normal checkbox */}
                  <div className="col-12 g-mb-25">
                    <label className="g-color-gray-dark-v2" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} style={{ marginTop: 3 }} />
                      <span>
                        I understand this request may be read by ministry team members and I may be contacted if I provide my contact info.
                      </span>
                    </label>
                  </div>

                  <div className="col-12">
                    <button type="submit" className="btn btn-md u-btn-primary rounded-0" disabled={status.state === "loading"}>
                      {status.state === "loading" ? "Submitting..." : "Submit Request"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-md u-btn-outline-black rounded-0 g-ml-10"
                      onClick={() => {
                        resetForm();
                        setStatus({ state: "idle", message: "" });
                      }}
                      disabled={status.state === "loading"}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="g-mt-20 g-font-size-13 g-color-gray-dark-v2">
              If this is urgent or an emergency, please contact local authorities or your nearest emergency service.
            </div>
          </div>
        </div>
      </section>

      <Podcast />
      <CoreValues />
      <Salvation />
      <Mission />
      <News />
      <Footer />
    </>
  );
}